import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  IconButton,
  Tabs,
  Tab,
  InputBase,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  Search as SearchIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Bookmark as BookmarkIcon,
  Article as ArticleIcon,
  Lightbulb as LightbulbIcon,
  AccountTree as AccountTreeIcon,
  QuestionAnswer as QuestionAnswerIcon,
  MenuBook as MenuBookIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { OutputFormat, getOutputFormatIcon } from '../components/ContentDisplay';
import { ContentCategory, OutputLanguage } from '../services/contentService';
import TimeSavedIndicator from '../components/TimeSavedIndicator';

// Interface for saved capsule
interface SavedCapsule {
  id: string;
  videoId: string;
  videoTitle: string;
  videoThumbnail: string;
  outputFormat: OutputFormat;
  language: OutputLanguage;
  category: ContentCategory;
  createdAt: string;
  timeSaved: number;
}

const LibraryPage: React.FC = () => {
  
  // State for tabs
  const [tabValue, setTabValue] = useState<number>(0);
  
  // State for search
  const [searchQuery, setSearchQuery] = useState<string>('');
  
  // State for menu
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCapsule, setSelectedCapsule] = useState<SavedCapsule | null>(null);
  
  // State for loading
  const [isLoading, setIsLoading] = useState<boolean>(true);
  
  // State for capsules
  const [capsules, setCapsules] = useState<SavedCapsule[]>([]);
  
  // Load capsules
  useEffect(() => {
    const loadCapsules = async () => {
      setIsLoading(true);
      
      try {
        // In a real implementation, you would fetch the user's saved capsules from the database
        // For now, we'll use mock data
        const mockCapsules: SavedCapsule[] = [
          {
            id: 'capsule1',
            videoId: 'dQw4w9WgXcQ',
            videoTitle: 'Introduction to Machine Learning',
            videoThumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            outputFormat: 'fullCapsule',
            language: OutputLanguage.English,
            category: ContentCategory.Educational,
            createdAt: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
            timeSaved: 45
          },
          {
            id: 'capsule2',
            videoId: 'dQw4w9WgXcQ',
            videoTitle: 'Advanced React Hooks Tutorial',
            videoThumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            outputFormat: 'keyInsights',
            language: OutputLanguage.English,
            category: ContentCategory.Educational,
            createdAt: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
            timeSaved: 30
          },
          {
            id: 'capsule3',
            videoId: 'dQw4w9WgXcQ',
            videoTitle: 'Latest Tech News Roundup',
            videoThumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            outputFormat: 'mindMap',
            language: OutputLanguage.Hinglish,
            category: ContentCategory.News,
            createdAt: new Date(Date.now() - 259200000).toISOString(), // 3 days ago
            timeSaved: 25
          },
          {
            id: 'capsule4',
            videoId: 'dQw4w9WgXcQ',
            videoTitle: 'Understanding Quantum Computing',
            videoThumbnail: 'https://img.youtube.com/vi/dQw4w9WgXcQ/maxresdefault.jpg',
            outputFormat: 'studyNotes',
            language: OutputLanguage.English,
            category: ContentCategory.Educational,
            createdAt: new Date(Date.now() - 345600000).toISOString(), // 4 days ago
            timeSaved: 60
          }
        ];
        
        setCapsules(mockCapsules);
      } catch (err) {
        console.error('Error loading capsules:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadCapsules();
  }, []);
  
  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Handle search change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };
  
  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, capsule: SavedCapsule) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedCapsule(capsule);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Handle delete capsule
  const handleDeleteCapsule = () => {
    if (selectedCapsule) {
      // In a real implementation, you would delete the capsule from the database
      setCapsules(capsules.filter(capsule => capsule.id !== selectedCapsule.id));
    }
    
    handleMenuClose();
  };
  
  // Handle share capsule
  const handleShareCapsule = () => {
    // In a real implementation, you would open a share dialog
    console.log('Share capsule:', selectedCapsule);
    
    handleMenuClose();
  };
  
  // Handle download capsule
  const handleDownloadCapsule = () => {
    // In a real implementation, you would download the capsule
    console.log('Download capsule:', selectedCapsule);
    
    handleMenuClose();
  };
  
  // Filter capsules based on tab and search query
  const filteredCapsules = capsules.filter(capsule => {
    // Filter by tab
    if (tabValue === 1 && capsule.category !== ContentCategory.Educational) return false;
    if (tabValue === 2 && capsule.category !== ContentCategory.News) return false;
    if (tabValue === 3 && capsule.category !== ContentCategory.Sports) return false;
    if (tabValue === 4 && capsule.category !== ContentCategory.Podcast) return false;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        capsule.videoTitle.toLowerCase().includes(query) ||
        capsule.category.toLowerCase().includes(query) ||
        capsule.language.toLowerCase().includes(query)
      );
    }
    
    return true;
  });
  
  // Get output format icon
  const getFormatIcon = (format: OutputFormat) => {
    const iconName = getOutputFormatIcon(format);
    
    switch (iconName) {
      case 'Article':
        return <ArticleIcon />;
      case 'Lightbulb':
        return <LightbulbIcon />;
      case 'AccountTree':
        return <AccountTreeIcon />;
      case 'Chat':
        return <QuestionAnswerIcon />;
      case 'MenuBook':
        return <MenuBookIcon />;
      default:
        return <ArticleIcon />;
    }
  };
  
  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          mb: 4
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <BookmarkIcon sx={{ mr: 1 }} />
          <Typography variant="h5" component="h1">
            My Library
          </Typography>
        </Box>
        
        {/* Search and Filter */}
        <Box sx={{ display: 'flex', mb: 3 }}>
          <Paper
            component="form"
            sx={{ 
              p: '2px 4px', 
              display: 'flex', 
              alignItems: 'center', 
              width: 400,
              borderRadius: 4
            }}
          >
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search your capsules"
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
              <SearchIcon />
            </IconButton>
          </Paper>
          
          <Box sx={{ ml: 'auto' }}>
            <IconButton aria-label="filter">
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Tabs */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 3 }}
        >
          <Tab label="All" />
          <Tab label="Studies" />
          <Tab label="News" />
          <Tab label="Sports" />
          <Tab label="Podcast" />
        </Tabs>
        
        <Divider sx={{ mb: 3 }} />
        
        {/* Capsules Grid */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : filteredCapsules.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No capsules found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchQuery ? 'Try a different search query' : 'Create your first capsule to get started'}
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredCapsules.map((capsule) => (
              <Grid item xs={12} sm={6} md={4} key={capsule.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardActionArea 
                    component={Link}
                    to={`/capsule/${capsule.id}`}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={capsule.videoThumbnail}
                        alt={capsule.videoTitle}
                      />
                      
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8
                        }}
                      >
                        <TimeSavedIndicator minutes={capsule.timeSaved} variant="compact" size="small" />
                      </Box>
                      
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          left: 8,
                          bgcolor: 'background.paper',
                          borderRadius: '50%',
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.main'
                        }}
                      >
                        {getFormatIcon(capsule.outputFormat)}
                      </Box>
                    </Box>
                    
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" component="div" gutterBottom noWrap>
                        {capsule.videoTitle}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', mb: 1 }}>
                        <Chip 
                          label={capsule.category} 
                          size="small" 
                          sx={{ mr: 1 }} 
                        />
                        <Chip 
                          label={capsule.language} 
                          size="small" 
                          variant="outlined" 
                        />
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 'auto' }}>
                        Created on {formatDate(capsule.createdAt)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                    <IconButton 
                      aria-label="more options" 
                      onClick={(e) => handleMenuOpen(e, capsule)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
      
      {/* Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleShareCapsule}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadCapsule}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteCapsule}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default LibraryPage;
